import { Ellipsis, MarketingItem } from '@wix/package-picker-components';
import { ExperimentsBag } from '@wix/wix-experiments';
import { Experiments, PaymentCycle, ProductChangeType } from 'enums';
import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { buildEnterpriseUrlLink, getDomainName } from 'utils';
import { EnterpriseLanguageToFormURL } from '../constants';
import { AdvancedPlansProductModel } from '../models/AdvancedPlansProductModel';
import { useEnterpriseModalContext } from '../providers/EnterpriseModalContextProvider';
import { EnterpriseModalClickSource } from '../services/BiLogger/BiLoggerTypes';
import { AppStore } from '../stores/App.store';
import {
  AdvancedPlansProductData,
  OfferingCardsMarketingItems,
  ProductCardData,
} from '../types';

export function getProductFeaturesItems(
  productId: string,
  offeringMarketingItems: OfferingCardsMarketingItems | undefined = {},
  experiments: ExperimentsBag,
): MarketingItem[] {
  const rawFeatures = offeringMarketingItems[productId] ?? [];
  const domainName = getDomainName(experiments);
  return rawFeatures
    .filter((feature) => feature.translationKey)
    .sort((f1, f2) => f1.order - f2.order)
    .map(({ translationKey, prefixIconUrl, suffixIconUrl, namespace }) => {
      return {
        text: (
          <Trans
            i18nKey={translationKey}
            values={{ domainName }}
            components={{
              ellipsis: (
                <Ellipsis
                  text={domainName ?? ''}
                  componentProps={{ size: 'small', weight: 'bold' }}
                />
              ),
            }}
          />
        ),
        prefixIconUrl,
        suffixIconUrl,
        namespace,
      };
    });
}

export interface UseProductCardsComponentsConfig {
  Hat: React.ElementType;
  Title: React.ElementType;
  Cta: React.ElementType;
  Price: React.ElementType;
}

export interface UseProductCardsProps {
  baseProducts: AdvancedPlansProductData[];
  onSelectProduct: (product: AdvancedPlansProductData) => void;
  appStore: AppStore;
  clickSource: EnterpriseModalClickSource;
  minimizedDisplay?: boolean;
}

interface IUseProductCards extends UseProductCardsProps {
  componentsConfig: UseProductCardsComponentsConfig;
}

export const useProductCards = ({
  baseProducts,
  onSelectProduct,
  clickSource,
  appStore,
  minimizedDisplay = false,
  componentsConfig,
}: IUseProductCards): ProductCardData[] => {
  const { t } = useTranslation();
  const {
    appContext,
    isInSale,
    isAnonymous,
    isChangePlan,
    isResellerOffering,
  } = appStore;

  const { openModal } = useEnterpriseModalContext();
  const advancedProducts = React.useMemo(
    () =>
      baseProducts.map(
        (product) => new AdvancedPlansProductModel(product, appStore),
      ),
    [appStore, baseProducts, appStore.currentCycle],
  );

  const {
    offeringCardMarketingItems,
    experiments,
    locale,
    userGuid,
    clientId,
  } = appContext;

  const isChangePlanNewContentEnabled =
    experiments[Experiments.ChangePlanNewContent] === 'true';

  const isCycleSelectorInPlansPage =
    appStore.isCycleSelectorsInProductsPageEnabled();

  const handleCTAOnClick = () => {
    const url = EnterpriseLanguageToFormURL.get(locale);

    if (url) {
      const enterpriseUrlLink = buildEnterpriseUrlLink({
        localeUrl: url,
        referrer: 'studio-pp-top',
        locale,
        userGuid,
        clientId,
      });
      window.open(enterpriseUrlLink, '_blank');
    } else {
      openModal(clickSource);
    }
  };

  const disabledTooltip = (product: AdvancedPlansProductModel) => {
    if (
      isCycleSelectorInPlansPage &&
      product.isSelectedCycleCurrentPlanCycle()
    ) {
      return t(
        'premium-purchase-plan.package_picker_and_cycle_selector.change_cycle_tooltip',
        {
          cycle: appStore.currentCycle,
        },
      );
    }

    return product.isCurrentPlanSameProductFamily &&
      isChangePlanNewContentEnabled
      ? t('premium-purchase-plan.change-plan-sunrise.tooltip')
      : t('Advanced.Plans_second.upgrade_plan.disabled.tooltip');
  };

  return advancedProducts.map((product) => {
    const productId = product.getId() ?? '';
    const productGuid = product.getGuid() ?? '';
    const isRecommended = !!product.getPopular();
    const isCurrentPlan = product.isCurrentPlanProduct;
    const isPlanDisabled = !product.isPurchaseEnabled();
    const isEnterpriseProduct = product.isEnterprise();

    const isSaleSupportedForCyclesInPlansPage =
      !isCycleSelectorInPlansPage ||
      (isCycleSelectorInPlansPage &&
        appStore.currentCycle !== PaymentCycle.MONTHLY);

    const isSaleSupportedInChangePlan =
      !isChangePlan ||
      product.getProductChangeType() === ProductChangeType.Upgrade;

    const isSaleEnabledForProduct =
      product.isProductInSale || product.getFirstCycleYearlyMonthly();

    const isOnSale = !!(
      isSaleSupportedForCyclesInPlansPage &&
      isInSale &&
      isSaleEnabledForProduct &&
      isSaleSupportedInChangePlan
    );

    const productFeatures = getProductFeaturesItems(
      productGuid,
      offeringCardMarketingItems,
      experiments,
    );

    const unifiedChangePlanPhase1Enabled =
      experiments[Experiments.UnifiedChangePlanPhase1] === 'true';
    const isUnifiedChangePlan = unifiedChangePlanPhase1Enabled && isChangePlan;
    const shouldShowDowngradeWarning =
      product.getProductChangeType() === ProductChangeType.Downgrade &&
      isUnifiedChangePlan &&
      !isPlanDisabled &&
      !minimizedDisplay;

    const isAllProductsOnSale =
      advancedProducts?.every((p) => Boolean(p.getSaleDiscountPercentage())) &&
      !isChangePlan;

    const salePercentages = isSaleSupportedInChangePlan
      ? product.getSaleDiscountPercentage()
      : undefined;

    const price = isEnterpriseProduct ? null : (
      <componentsConfig.Price
        product={product}
        size={minimizedDisplay ? 'tiny' : 'small'}
      />
    );

    const hat = (
      <componentsConfig.Hat
        isCurrentPlan={isCurrentPlan}
        isRecommended={isRecommended}
        text={product.getHatLabel()}
        salePercentages={salePercentages}
        isAllProductsOnSale={isResellerOffering && isAllProductsOnSale}
      />
    );

    const title = (
      <componentsConfig.Title
        title={product.getName()}
        {...(minimizedDisplay
          ? { size: 'small' }
          : { size: 'medium', subTitle: t(product.getDescription()) })}
      />
    );

    const cta = !isAnonymous && (
      <componentsConfig.Cta
        text={
          isEnterpriseProduct
            ? t('premium-purchase-plan.cards.enterprise.cta', 'Contact Us')
            : t('productEntry.select', 'Select')
        }
        size={minimizedDisplay ? 'small' : 'medium'}
        disabled={isPlanDisabled}
        fullColor={isRecommended}
        onClick={
          isEnterpriseProduct
            ? () => handleCTAOnClick()
            : () => onSelectProduct(product.getBaseProduct())
        }
        dataHook={
          isEnterpriseProduct ? 'enterprise-select-button' : 'select-button'
        }
        tooltipContent={disabledTooltip(product)}
        shouldShowDowngradeWarning={shouldShowDowngradeWarning}
      />
    );

    return {
      data: {
        guid: productGuid,
        id: productId,
        features: productFeatures,
        isRecommended,
        isCurrentPlan,
        isOnSale,
        isShowingSavings: isCycleSelectorInPlansPage,
      },
      components: { title, hat, price, cta },
    };
  });
};
